"use client";

import React, { useRef, useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const stages = [{
  title: "Request Quote",
  description: "Submit your project requirements through our enterprise portal to receive a tailored sanitation strategy and pricing."
}, {
  title: "Match & Align",
  description: "Our national network identifies the premium units and certified operators perfectly suited for your specific site needs."
}, {
  title: "Confirm Logistics",
  description: "Finalize your deployment schedule with our dedicated coordinators to ensure seamless site preparation and timing."
}, {
  title: "Delivery & Service",
  description: "Experience precision deployment of units followed by rigorous maintenance and professional on-site management."
}];
export default function OurCollaborativeJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollContainerRef.current;
      const progress = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) * 100 : 0;
      setScrollProgress(progress);
    }
  };
  useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, []);
  return <section data-section-id="222526" id="the-logistics-blueprint" className="dark bg-background text-foreground py-24 md:py-32 overflow-hidden relative border-y border-border">
      <div className="container mx-auto px-4 mb-16 md:mb-24 relative z-20">
        <div ref={headerRef} className={`max-w-3xl transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-6xl font-default font-bold uppercase tracking-tight mb-6">
            The Logistics <span className="text-primary">Blueprint</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans max-w-2xl">
            A high-performance framework for enterprise site services. From initial request to national deployment, we follow a rigorous, tech-forward process to ensure your project stays on schedule.
          </p>
        </div>
      </div>

      {}
      <div className="relative w-full">
        {}
        <div className="absolute top-1/2 left-0 w-full h-[2px] bg-border/30 -translate-y-1/2 z-0 hidden md:block" />
        
        {}
        <div className="absolute top-1/2 left-0 h-[2px] bg-primary -translate-y-1/2 z-0 transition-all duration-75 ease-out hidden md:block" style={{
        width: `${scrollProgress}%`
      }} />

        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory pb-16 pt-8 md:pt-32 md:pb-32 px-4 md:px-[10vw] gap-8 md:gap-24 relative z-10" style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none"
      }}>
          {}
          <style dangerouslySetInnerHTML={{
          __html: `
            div::-webkit-scrollbar { display: none; }
          `
        }} />

          {stages.map((stage, i) => <div key={i} data-index={i} className="min-w-[85vw] md:min-w-[450px] lg:min-w-[550px] snap-center relative flex flex-col justify-center group">
              {}
              <div className="absolute top-0 md:top-1/2 left-0 md:-translate-y-1/2 text-[120px] md:text-[200px] font-default font-bold text-transparent z-[-1] transition-transform duration-700 group-hover:scale-105 pointer-events-none select-none opacity-10" style={{
            WebkitTextStroke: "2px var(--primary)"
          }}>
                0{i + 1}
              </div>

              {}
              <div className="bg-background/60 backdrop-blur-md border border-border/50 p-8 md:p-12 mt-16 md:mt-0 transition-all duration-300 hover:border-primary/40 hover:bg-background/80">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-8 h-[2px] bg-primary" />
                  <h3 className="text-2xl md:text-3xl font-default font-bold uppercase tracking-wide text-foreground">
                    {stage.title}
                  </h3>
                </div>
                <p className="text-muted-foreground text-base md:text-lg font-sans leading-relaxed">
                  {stage.description}
                </p>
              </div>
            </div>)}

          {}
          <div className="min-w-[10vw] md:min-w-[20vw] shrink-0" aria-hidden="true" />
        </div>

        {}
        <div className="absolute bottom-4 right-8 flex items-center gap-2 text-primary animate-pulse md:hidden z-20">
          <span className="text-sm font-mono uppercase tracking-widest">Scroll</span>
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </section>;
}